import axios from "axios";
import { RightTrim } from "./Sanitation";

// TODO create a link builder that accepts the root URL as a prop and can build all other URLs from that one bit of info

const RootUsersPathSegment = "Users";
const RootImportPathSegment = "Import";
const RootInteractionsPathSegment = "Interactions";

/**
 * Encapsulates all user management API requests
 *
 *<pre><code>
 * import MentorApplicationApi from "./lib/MentorApplicationApi.js"
 * let userManagementActions = new MentorApplicationApi(someUrl);
 * userManagementActions.addRole('user@domain.com', 'Mentor') // returns Axios promise
 * .then(
 *   // deal with success and error branches
 *   success => someLogic,
 *   error => someOtherLogic
 * )
 * .catch() // deal with any thrown errors
 * </code></pre>
 */
export default class MentorApplicationApi {
  /**
   * pass in the URL that manages POST (create) and DELETE (delete) requests
   * for roles
   * @param apiRootUrl
   */
  constructor(apiRootUrl) {
    this.apiRootUrl = RightTrim("/", apiRootUrl);
    this.profiles = {
      main: "profile",
      student: "studentProfile",
      mentor: "mentorProfile"
    };
  }

  /**
   * Get the API call URL
   * @param {string[]} segments
   * @returns {string}
   */
  getUrl(segments) {
    return `${this.apiRootUrl}/${segments
      .map(x => encodeURIComponent(x))
      .join("/")}`;
  }

  /**
   * Submit a change major form
   * @param {string} studentGuid
   * @param {string} newMajor
   * @param {string} reasonWhy
   * @param {boolean} beContacted
   * @param otherCollege
   * @param otherCollegeMajor
   * @param otherCollege
   * @param otherCollegeMajor
   * @returns {Promise<AxiosResponse<any>>}
   */
  changeMajor(
    studentGuid,
    newMajor,
    reasonWhy,
    beContacted,
    otherCollege,
    otherCollegeMajor
  ) {
    // TODO variable names should be consistent
    // noinspection SpellCheckingInspection
    return axios.post(
      this.getUrl([RootUsersPathSegment, studentGuid, "changemajor"]),
      {
        newMajor: newMajor,
        comments: reasonWhy,
        contactMe: beContacted,
        otherCollege: otherCollege,
        otherCollegeMajor: otherCollegeMajor
      }
    );
  }

  /**
   * Send an email message to one or more recipients
   * @param {string} userGuid current user id
   * @param {array} recipients MUST be a list of recipient email addresses
   * @param {string} subject
   * @param {string} body
   * @returns {Promise<AxiosResponse<any>>}
   */
  sendMessage(userGuid, recipients, subject, body) {
    return axios.post(
      this.getUrl([RootUsersPathSegment, userGuid, "messages"]),
      {
        subject: subject,
        body: body,
        recipients: recipients
      }
    );
  }

  /**
   * Send an email message to everyone in the provided roles
   * @param {string[]} recipientRoles
   * @param {string} subject
   * @param {string} body
   * @returns {Promise<AxiosResponse<any>>}
   */
  sendBulkMessage(recipientRoles, subject, body) {
    return axios.post(this.getUrl(["Messages", "Bulk"]), {
      subject: subject,
      body: body,
      recipientRoles: recipientRoles
    });
  }

  /**
   * Log an interaction
   * @param {string} mentorGuid
   * @param {string[]} studentIds
   * @param {string} startTime ISO formatted date time
   * @param {number} duration seconds
   * @param {string} notes
   * @param {string} type
   * @param {string[]} topics
   * @param {string} tag
   * @returns {Promise<AxiosResponse<any>>}
   */
  logInteraction(
    mentorGuid,
    studentIds,
    startTime,
    duration,
    notes,
    type,
    topics,
    tag
  ) {
    return axios.post(
      this.getUrl([RootUsersPathSegment, mentorGuid, "interactions"]),
      {
        studentIds: studentIds,
        startTime: startTime,
        durationInSeconds: duration,
        notes: notes,
        interactionType: type,
        topics: topics,
        tag: tag
      }
    );
  }

  /**
   * Update a user attribute
   * @param {string} userGuid
   * @param {string} profilePart valid values: profile, studentProfile, mentorProfile
   * @param {string} prop
   * @param {*} value
   * @returns {Promise<AxiosResponse<any>>}
   */
  updateProfile(userGuid, profilePart, prop, value) {
    return axios.patch(
      this.getUrl([RootUsersPathSegment, userGuid, profilePart, prop]),
      {
        value: value
      }
    );
  }

  /**
   * Default profile property saver
   * @param {function} resolve
   * @param {function} reject
   * @param {string} userGuid
   * @param {string} profileName
   * @param {string} name
   * @param {*} value
   * @param profile
   */
  propertySaver(resolve, reject, userGuid, profileName, name, value, profile) {
    this.updateProfile(userGuid, profileName, name, value)
      .then(response => {
        resolve(response);
        profile[name] = response.data.newValue;
      })
      .catch(() => reject("server error - unable to update"));
  }

  /**
   * Update a phone number
   * @param {string} userGuid
   * @param {number} phoneNumberId
   * @param {object} changedProperties
   * @returns {Promise<AxiosResponse<any>>}
   */
  updatePhoneNumber(userGuid, phoneNumberId, changedProperties) {
    return axios.patch(
      this.getUrl([
        RootUsersPathSegment,
        userGuid,
        "phoneNumbers",
        phoneNumberId
      ]),
      {
        changedProperties
      }
    );
  }

  /**
   * Update one or more profile values
   * @param {string} userGuid
   * @param {object} changedProperties
   */
  bulkUpdateUserProfileProps(userGuid, changedProperties) {
    return axios.patch(this.getUrl([RootUsersPathSegment, userGuid]), {
      changedProperties
    });
  }

  /**
   * @param {string} searchText
   * @param {string[]|null} [roles=null]
   * @returns {Promise<AxiosResponse<any>>}
   */
  userSearch(searchText, roles = null) {
    let params = {
      SearchText: searchText.trim()
    };
    // only set the roles value if something is actually provided
    if (null !== roles) {
      params.Roles = roles;
    }

    /**
     * Inner method to manage sensibly serializing query param values
     * @param val
     * @returns {string}
     */
    function serializeValue(val) {
      if ("string" === typeof val) {
        return val;
      } else {
        return JSON.stringify(val);
      }
    }
    return axios.get(this.getUrl([RootUsersPathSegment]), {
      params: params,
      // custom serializer is necessary because dotnet core expects query param
      // arrays to include an index, e.g. `Roles[0]=x&Roles[1]=y` instead of `Roles[]=x&Roles[]=y`
      paramsSerializer: params => {
        return Object.keys(params)
          .flatMap(paramKey => {
            let paramValue = params[paramKey];
            // special handling for arrays
            if (Array.isArray(paramValue)) {
              return paramValue.map((currentValue, idx) => {
                return `${encodeURIComponent(
                  paramKey
                )}[${idx}]=${encodeURIComponent(serializeValue(currentValue))}`;
              });
            }
            // everything else
            return `${encodeURIComponent(paramKey)}=${encodeURIComponent(
              serializeValue(paramValue)
            )}`;
          })
          .join("&");
      }
    });
  }

  /**
   * Add a role to a user
   * @param {string} id
   * @param {string} email
   * @param {string} role
   * @returns {Promise<AxiosResponse<any>>}
   */
  addRole(id, email, role) {
    return axios.post(this.getUrl([RootUsersPathSegment, id, "roles"]), {
      email: email,
      role: role
    });
  }

  /**
   * Remove a student/mentor link
   *
   * Requires both the student email and ID as a way to ensure no mistakes when
   * making such an important change.
   * @param {string} mentorId
   * @param {string} studentEmail
   * @param {string} studentId
   * @returns {Promise<AxiosResponse<any>>}
   */
  removeStudentMentorLink(mentorId, studentEmail, studentId) {
    return axios.delete(
      this.getUrl([RootUsersPathSegment, mentorId, "Students"]),
      {
        data: {
          id: studentId,
          email: studentEmail
        }
      }
    );
  }

  /**
   * Add a student/mentor link
   *
   * Requires both the student email and ID as a way to ensure no mistakes when
   * making such an important change.
   * @param {string} mentorId
   * @param {string} studentEmail
   * @param {string} studentId
   * @returns {Promise<AxiosResponse<any>>}
   */
  addStudentMentorLink(mentorId, studentEmail, studentId) {
    return axios.post(
      this.getUrl([RootUsersPathSegment, mentorId, "Students"]),
      {
        id: studentId,
        email: studentEmail
      }
    );
  }

  /**
   * Remove a role from a user
   * @param {string} id
   * @param {string} email
   * @param {string} role
   * @returns {Promise<AxiosResponse<any>>}
   */
  removeRole(id, email, role) {
    return axios.delete(this.getUrl([RootUsersPathSegment, id, "roles"]), {
      data: { email: email, role: role }
    });
  }

  /**
   * Delete a phone number
   * @param {string} userGuid
   * @param {number} phoneNumberId
   * @returns {Promise<AxiosResponse<any>>}
   */
  deletePhoneNumber(userGuid, phoneNumberId) {
    return axios.delete(
      this.getUrl([
        RootUsersPathSegment,
        userGuid,
        "phoneNumbers",
        phoneNumberId
      ])
    );
  }

  /**
   * Return a phone number
   * @param {string} userGuid
   * @param {object} phoneNumberData
   * @returns {Promise<AxiosResponse<any>>}
   */
  addPhoneNumber(userGuid, phoneNumberData) {
    return axios.post(
      this.getUrl([RootUsersPathSegment, userGuid, "phoneNumbers"]),
      phoneNumberData
    );
  }

  /**
   * Upload an import file
   * @param {string} userType
   * @param {object} rawFormData form fields and values (each item to be appended to a FormData object)
   * @returns {Promise<AxiosResponse<any>>}
   */
  importUsers(userType, rawFormData) {
    let formData = new FormData();
    Object.keys(rawFormData).forEach(key => {
      formData.append(key, rawFormData[key]);
    });
    return axios.post(
      this.getUrl([RootImportPathSegment, userType]),
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" }
      }
    );
  }
  /**
   * Add New User
   * @param {object} userData
   * @returns {Promise<import("axios").AxiosResponse<any>>}
   */
  userAdd(userData) {
    return axios.post(this.getUrl([RootUsersPathSegment]), userData);
  }

  /**
   * Get interactions
   * @param requestData
   * @returns {Promise<AxiosResponse<any>>}
   */
  getInteractions(requestData) {
    return axios.post(this.getUrl([RootInteractionsPathSegment]), requestData);
  }
}
